import React, { useEffect } from 'react'
import { useStoreState } from 'easy-peasy'
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Button,
  Menu,
  MenuList,
  MenuItem,
  MenuButton
} from '@chakra-ui/react'
import ReportProblem from '@material-ui/icons/ReportProblem'
import { Mic, MicOff } from '@components/icons'
import {
  ArrowDownIcon,
  RadioSelectedIcon,
  NoHeadsetIcon,
  Headphones,
  InPersonFilled,
  Online
} from '@blueprinthq/joy'
import {
  DialogActions,
  DialogHeader,
  DialogBody
} from '@handlers/sessions/components/dialog'
import { useExperienceManager, useAudioCapture } from '@hooks'

import { endpoints } from '@api'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { isMobile } from 'react-device-detect'

type ReturnUserStartRecordingDialogPropsT = {
  onClose: () => void
  startRecording: () => void
  handleShareAudio: () => Promise<void>
  isExtension: boolean
}

type RouteParams = {
  id: string
  sessionId: string
}

export const ReturnUserStartRecordingDialog = ({
  onClose,
  handleShareAudio,
  startRecording,
  isExtension
}: ReturnUserStartRecordingDialogPropsT) => {
  const {
    audioInputs,
    isTelehealth,
    setIsTelehealth,
    isUsingHeadphones,
    setIsUsingHeadphones,
    selectedAudioInput,
    setSelectedAudioInput
  } = useAudioCapture()

  const { isSafari } = useExperienceManager()
  const { audioLevel } = useStoreState(state => state.volume)
  const { id: clientId } = useParams<RouteParams>()
  
  const showSafariWarning = isSafari && !isMobile

  const { data: lastSession }: any = useQuery(
    [endpoints.getClinicianClientLastSession.getCacheId()],
    () =>
      endpoints.getClinicianClientLastSession.request({
        id: clientId
      })
  )

  useEffect(() => {
    if (lastSession) {
      setIsTelehealth(lastSession.isTelehealth)
      setIsUsingHeadphones(lastSession.isUsingHeadphones)
    }
  }, [lastSession])

  useEffect(() => {
    const lastMicDeviceId = localStorage.getItem('microphoneDeviceId')
    if (lastMicDeviceId) {
      setSelectedAudioInput(lastMicDeviceId)
    }
  }, [])

  const MicButton = () => {
    const selectedInput = audioInputs.find(
      input => input.deviceId === selectedAudioInput
    )
    return (
      <HStack spacing={3}>
        <Mic
          dy={1 - audioLevel}
          filterId="startRecording"
        />
        <Text color="black" overflow="hidden" textOverflow="ellipsis">
          {selectedInput?.label}
        </Text>
      </HStack>
    )
  }

  const HeadphonesButton = () => {
    if (isUsingHeadphones) {
      return (
        <HStack spacing={3}>
          <Headphones />
          <Text color="black">Using headphones</Text>
        </HStack>
      )
    }
    return (
      <HStack spacing={3}>
        <NoHeadsetIcon />
        <Text color="black">Not using headphones</Text>
      </HStack>
    )
  }

  const handleMicChange = (event: any) => {
    localStorage.setItem('microphoneDeviceId', event.target.value)
    setSelectedAudioInput(event.target.value)
  }

  const handleSettingChange = (isTelehealth: boolean) => {
    if (!isTelehealth) {
      setIsUsingHeadphones(false)
    }
    setIsTelehealth(isTelehealth)
  }

  const handleHeadphonesChange = (event: any) => {
    setIsUsingHeadphones(event.target.value === 'headphones')
  }

  return (
    <>
      <DialogActions onBack={onClose} />
      <DialogBody>
        <Box w="100%">
          <Box textAlign="center" mb={6}>
            <DialogHeader text="New Session" />
          </Box>
          <VStack w="100%" spacing="large">
            <VStack w="100%" spacing={4}>
              <Menu>
                <MenuButton
                  border="1px solid #E4E5E6"
                  m="0"
                  w="100%"
                  bg="white"
                  borderRadius="4px"
                  as={Button}
                  rightIcon={<ArrowDownIcon />}
                >
                  <MicButton />
                </MenuButton>
                <MenuList w="100%">
                  {audioInputs.map(input => (
                    <MenuItem
                      isDisabled={input.deviceId === selectedAudioInput}
                      w={'400px'}
                      key={input.deviceId}
                      onClick={handleMicChange}
                      value={input.deviceId}
                      color="black"
                      icon={
                        input.deviceId === selectedAudioInput ? 
                        <Mic
                          dy={1 - audioLevel}
                          filterId="startRecording"
                        /> :
                        <MicOff style={{ fill: 'black' }} />
                      }
                    >
                      {input.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Flex
                w="100%"
                h="46px"
                align="center"
                bg="#E4E5E6"
                borderRadius="4px"
                _hover={{ cursor: 'pointer' }}
                px={1}
              >
                <HStack
                  justifyContent="center"
                  spacing={3}
                  w="50%"
                  h="38px"
                  bg={isTelehealth ? 'inherit' : 'white'}
                  borderRadius="4px"
                  onClick={() => handleSettingChange(false)}
                >
                  <InPersonFilled fill={isTelehealth ? '#757575' : 'black'} />
                  <Text color={isTelehealth ? '#757575' : 'black'}>
                    In-Person
                  </Text>
                </HStack>
                <HStack
                  justifyContent="center"
                  spacing={3}
                  w="50%"
                  h="38px"
                  bg={isTelehealth ? 'white' : 'inherit'}
                  borderRadius="4px"
                  onClick={() => handleSettingChange(true)}
                >
                  <Online fill={isTelehealth ? 'black' : '#757575'} />
                  <Text color={isTelehealth ? 'black' : '#757575'}>
                    Telehealth
                  </Text>
                </HStack>
              </Flex>
              {isTelehealth && (
                <>
                  <Menu placement="bottom-start">
                    <MenuButton
                      border="1px solid #E4E5E6"
                      m="0"
                      w="100%"
                      bg="white"
                      borderRadius="4px"
                      as={Button}
                      rightIcon={<ArrowDownIcon />}
                    >
                      <HeadphonesButton />
                    </MenuButton>
                    <MenuList w="100%">
                      <MenuItem
                        isDisabled={isUsingHeadphones}
                        onClick={handleHeadphonesChange}
                        value="headphones"
                        color="black"
                        w={'400px'}
                        icon={<Headphones />}
                      >
                        Using headphones
                      </MenuItem>
                      <MenuItem
                        isDisabled={!isUsingHeadphones}
                        onClick={handleHeadphonesChange}
                        value="no_headphones"
                        color="black"
                        w={'400px'}
                        icon={<NoHeadsetIcon />}
                      >
                        Not using headphones
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </>
              )}
            </VStack>

            {isUsingHeadphones ? (
              <>
                {showSafariWarning && (
                <VStack spacing={6}>
                  <Box>
                    <ReportProblem style={{ height: 80, width: 80 }} />
                  </Box>
                    <DialogHeader text="Audio recording for telehealth sessions is not fully supported in Safari browser" />
                    <Text textAlign="center">
                      Due to a limitation of the Safari browser, the AI Notetaker
                      feature will not be able to capture participant audio duing a
                      telehealth session.
                    </Text>
                    <Text mt={6}>For the best results, please use Chrome.</Text>
                </VStack>
                )}
                <Button
                  aria-label="Start Recording"
                  bg="black"
                  isFullWidth
                  onClick={handleShareAudio}
                  size="lg"
                >
                  <HStack spacing={2}>
                    <RadioSelectedIcon fill="#68E19F" />
                    <Text fontWeight="bold" color="white">
                      {isExtension ? 'Share audio + record' : 'Share browser audio + begin recording'}
                    </Text>
                  </HStack>
                </Button>
              </>
            ) : (
              <Button
                aria-label="Start Recording"
                bg="black"
                onClick={startRecording}
                isFullWidth
                size="lg"
              >
                <HStack spacing={2}>
                  <RadioSelectedIcon fill="#68E19F" />
                  <Text fontWeight="bold" color="white">
                    Start Recording
                  </Text>
                </HStack>
              </Button>
            )}
          </VStack>
        </Box>
      </DialogBody>
    </>
  )
}
